import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import MenuIcon from '@mui/icons-material/Menu';
import { Box, IconButton, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import routes from 'routes';
import theme from 'App.theme';
import { ReactComponent as SAVILogo } from 'assets/savi_icon.svg';
import RestrictedComponent from 'components/RestrictedComponent/RestrictedComponent';
import SettingsModal from 'components/SettingsModal/SettingsModal';
import UserAvatar from 'components/UserAvatar/UserAvatar';
import { useLanguage, useSearch } from 'context/AppContext';
import { useAuth0User } from 'hooks/useAuth0User';
import useScreenSize from 'hooks/useScreenSize';
import appInfo from '../../../package.json';
import { getNavigationItems } from './Navigation.constants';
import {
  ActionPillsContainer,
  NavigationContainer,
  NavLinksContainer,
  NavListItem,
  NavListLogout,
} from './Navigation.styles';
import { useCheckForPartiallyCheckedInSets } from 'api/hooks/useCheckForPartiallyCheckedInSets';
import { useEditDeliveryMode } from 'hooks/useEditDeliveryMode';
import { useShowEditDeliveryExitWarningDialog } from 'hooks/useShowEditDeliveryExitWarningDialog';
import VersionUpdateBanner from 'components/VersionUpdateBanner';
import LogoutButton from 'components/LogoutButton';
import Button from 'components/Button';
import { HELP_BUTTON_ON } from 'featureFlags';
import { TenantType } from 'api/models/Users';
import { useCurrentUser } from 'api/hooks/useCurrentUser';
import useNavigationMinimized from 'hooks/navigationMinimized';

const navigationItems = getNavigationItems();
// Smartways operations users only get the All Orders page
const adminOperationsNavigationItems: typeof navigationItems = ['AllOrders'];

const checkActive = (locationPath: string, linkPath: string): boolean => {
  return locationPath.split('/')[1] === linkPath.split('/')[1];
};

export const Navigation: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [language] = useLanguage();
  const auth0User = useAuth0User();
  const { data: user } = useCurrentUser();
  const isHospitalUser = user?.tenant.type == TenantType.HOSPITAL;
  // no items until the tenant is known (loading or failed), so operations users never see the full menu
  const visibleNavigationItems = !user
    ? []
    : user.tenant.type == TenantType.ADMIN_OPERATIONS
    ? adminOperationsNavigationItems
    : navigationItems;
  const [, dispatchSearch] = useSearch();
  const checkForPartiallyCheckedInSets = useCheckForPartiallyCheckedInSets({ showCheckInExitWarningDialog: true });
  const [editDeliveryMode] = useEditDeliveryMode();
  const [_, showEditDeliveryExitWarningDialog] = useShowEditDeliveryExitWarningDialog();

  const [isMediumScreen, setIsMediumScreen] = useState<boolean>(false);
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(false);
  const [isCompactScreen, setIsCompactScreen] = useState<boolean>(false);
  const [isMobileSize, setIsMobileSize] = useState<boolean>(false);
  const [minimized, setMinimized] = useNavigationMinimized();
  const screenSize = useScreenSize();

  const [openSettingsModal, setOpenSettingsModal] = useState(false);

  const appVersionLabel = language.versionLabel.prefix + appInfo.version;

  useEffect(() => {
    const isSmall = screenSize.width < theme.breakpoints.values.sm;
    const isMedium = screenSize.width < theme.breakpoints.values.lg;

    if (!isSmallScreen) setIsMobileSize(isSmall);
    if (!isMediumScreen) setIsCompactScreen(isMedium);

    setIsSmallScreen(isSmall);
    setIsMediumScreen(isMedium);
  }, [screenSize.width, isSmallScreen, isMediumScreen]);

  const handleNavigation = useCallback(
    (route: string): void => {
      const navigateToRoute = () => {
        navigate(route);
        dispatchSearch('');
      };

      if (checkForPartiallyCheckedInSets(navigateToRoute)) return;

      if (editDeliveryMode) {
        showEditDeliveryExitWarningDialog({
          show: true,
          nextRoute: route,
        });

        return;
      }

      navigateToRoute();
    },
    [checkForPartiallyCheckedInSets, editDeliveryMode, navigate, dispatchSearch, showEditDeliveryExitWarningDialog],
  );

  const handleClickLogo = useCallback(() => {
    const destination = auth0User.isAuthenticated ? '' : routes.LoggedOut.makePath();

    if (checkForPartiallyCheckedInSets(() => navigate(destination))) return;

    navigate(destination);
  }, [checkForPartiallyCheckedInSets, navigate, auth0User.isAuthenticated]);

  const toggleSideMenuMobileOpen = useCallback(() => {
    setMinimized(!minimized);
  }, [minimized, setMinimized]);

  return (
    <>
      {isMobileSize && minimized ? (
        <>
          <div style={{ position: 'relative' }}>
            <IconButton
              onClick={toggleSideMenuMobileOpen}
              edge="start"
              aria-label="menu"
              size="large"
              style={{
                position: 'fixed',
                left: '20px',
                bottom: '10px',
                zIndex: theme.layers.top,
                backgroundColor: theme.colors.active,
                color: 'white',
              }}
            >
              <MenuIcon />
            </IconButton>
          </div>
        </>
      ) : (
        <NavigationContainer compact={isMediumScreen}>
          <Box className="logo-container" onClick={handleClickLogo}>
            <SAVILogo title="Logo" />
          </Box>

          <NavLinksContainer>
            <ActionPillsContainer>
              <UserAvatar onClick={() => setOpenSettingsModal(true)} />
              {HELP_BUTTON_ON && isHospitalUser && !!process.env.REACT_APP_SAVI_SUPPORT_URL && (
                <Button
                  label={language.Navigation.help}
                  colors="white on primary"
                  onClick={() => {
                    window.open(process.env.REACT_APP_SAVI_SUPPORT_URL);
                  }}
                  style={{
                    width: '64px',
                    height: '40px',
                    borderRadius: '20px',
                    margin: 0,
                  }}
                />
              )}
            </ActionPillsContainer>

            {visibleNavigationItems.map(name => {
              const route = routes[name];
              const { Icon, fill = 'svg', roles = [], featureFlag } = route;
              const path = route.path; // || route.makePath();
              return (
                <RestrictedComponent key={name} featureFlag={featureFlag} allowedRoles={roles}>
                  <NavListItem onClick={() => handleNavigation(path)} active={checkActive(location.pathname, path)}>
                    {Icon && <Icon className={fill == 'path' ? 'menu-icon-path-fill' : 'menu-icon'} />}
                    {!isCompactScreen && !isMobileSize && language.Routes[name]}
                  </NavListItem>
                </RestrictedComponent>
              );
            })}

            <NavListLogout>
              <LogoutButton showLabel={!isCompactScreen && !isMobileSize} />
              <Typography sx={{ fontSize: '12px', color: theme.greys.GREY3, paddingBottom: '10px' }}>
                {appVersionLabel}
              </Typography>
            </NavListLogout>

            {isMobileSize && (
              <IconButton
                onClick={toggleSideMenuMobileOpen}
                size="large"
                style={{
                  bottom: '10px',
                  marginTop: '10px',
                }}
              >
                <ArrowBackIosIcon />
              </IconButton>
            )}
          </NavLinksContainer>
          <SettingsModal
            open={openSettingsModal}
            close={() => setOpenSettingsModal(false)}
            isAuthenticated={auth0User.isAuthenticated}
          />
        </NavigationContainer>
      )}
      <VersionUpdateBanner appVersion={appInfo.version} />
    </>
  );
};
